import { useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Panel, StatusBadge } from '@/components/kit';
import { ListBody } from '@/components/inputs';
import LedgerDialog from '@/components/admin/LedgerDialog';
import {
  LEDGER_FIELDS,
  LEDGER_LABEL_STYLES,
  credits,
  hasNumbers,
  kindLabel,
  num,
  profitOf,
  signedMoney,
} from '@/shared/ledger';
import { dayOf, rows } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq } = window.ApperSDK.CoreSDK;

/** Ledger entries for one order: credits used, cost and collected, with a running total. */
export default function OrderLedger({ order, refreshKey = 0 }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data, loading, error, run } = useFetch(
    () =>
      sdk
        .table('ledger_c')
        .select(LEDGER_FIELDS)
        .where(eq('order_c', order.Id))
        .orderByDesc('entry_date_c')
        .limit(50)
        .fetch()
        .then(rows),
    [order.Id, refreshKey],
  );

  const list = data ?? [];
  const totals = list.reduce(
    (t, e) => ({
      credits: t.credits + (num(e.credits_c) ?? 0),
      cost: t.cost + (num(e.cost_c) ?? 0),
      collected: t.collected + (num(e.collected_c) ?? 0),
    }),
    { credits: 0, cost: 0, collected: 0 },
  );
  const profit = totals.collected - totals.cost;

  function edit(entry) {
    setEditing(entry);
    setEditOpen(true);
  }

  return (
    <Panel
      title="Ledger"
      icon="BookOpenCheck"
      bodyClassName="py-1"
      actions={
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
          <ApperIcon name="Plus" size={16} />
          Add entry
        </Button>
      }
    >
      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        rows={2}
        isEmpty={!list.length}
        empty={{
          icon: 'BookOpenCheck',
          title: 'No ledger entries',
          description: 'One is opened when you activate or extend this line. Add one by hand any time.',
          action: () => setCreateOpen(true),
          actionLabel: 'Add entry',
        }}
      >
        <ul className="divide-y divide-border">
          {list.map((e) => (
            <li key={e.Id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <StatusBadge value={kindLabel(e.kind_c)} styles={LEDGER_LABEL_STYLES} />
                  <span className="text-xs text-muted-foreground">
                    {e.entry_date_c ? formatLocalDate(dayOf(e.entry_date_c), DATE_FORMATS.SHORT) : 'No date'}
                  </span>
                </div>
                {e.notes_c && <p className="mt-1 truncate text-xs text-muted-foreground">{e.notes_c}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-3 text-sm tabular-nums">
                {hasNumbers(e) ? (
                  <>
                    <span title="Credits used">{credits(e.credits_c)} cr</span>
                    <span className="text-muted-foreground" title="Cost">
                      -{signedMoney(num(e.cost_c) ?? 0)}
                    </span>
                    <span title="Collected">+{signedMoney(num(e.collected_c) ?? 0)}</span>
                    <span className={profitOf(e) >= 0 ? 'font-semibold text-success' : 'font-semibold text-destructive'}>
                      {signedMoney(profitOf(e))}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-warning">Needs numbers</span>
                )}
                <Button variant="ghost" size="icon" aria-label="Edit ledger entry" onClick={() => edit(e)}>
                  <ApperIcon name="Pencil" size={16} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {list.length > 1 && (
          <div className="flex flex-wrap justify-end gap-x-4 gap-y-1 border-t border-border py-2.5 text-sm tabular-nums">
            <span>{credits(totals.credits)} credits</span>
            <span className="text-muted-foreground">Cost {signedMoney(totals.cost)}</span>
            <span>Collected {signedMoney(totals.collected)}</span>
            <span className={profit >= 0 ? 'font-semibold text-success' : 'font-semibold text-destructive'}>
              Kept {signedMoney(profit)}
            </span>
          </div>
        )}
      </ListBody>

      <LedgerDialog open={createOpen} onOpenChange={setCreateOpen} order={order} onSaved={run} />
      <LedgerDialog open={editOpen} onOpenChange={setEditOpen} entry={editing} order={order} onSaved={run} />
    </Panel>
  );
}
